import React, { useState } from 'react';
import { api, setToken } from '../api/client';

const TITLES = {
  'forgot-password': 'Forgot password',
  'forgot-username': 'Forgot username',
  'reset': 'Choose a new password',
};

export default function Login({ onLogin, resetToken }) {
  const [mode, setMode] = useState(resetToken ? 'reset' : 'login');
  const [form, setForm] = useState({ username: '', email: '', password: '', identifier: '', confirm: '' });
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  const switchMode = (next) => {
    // Leaving the reset screen drops the token from the address bar
    if (mode === 'reset') window.history.replaceState(null, '', window.location.pathname);
    setMode(next);
    setError('');
    setNotice('');
  };

  const submitLogin = async () => {
    try {
      if (mode === 'login') {
        const data = await api.login(form.username, form.password);
        setToken(data.access_token);
        onLogin();
      } else {
        await api.register(form);
        const data = await api.login(form.username, form.password);
        setToken(data.access_token);
        onLogin();
      }
    } catch (e) {
      setError(mode === 'login' ? 'Invalid username or password.' : 'Registration failed. Username or email may already exist.');
    }
  };

  const submitRecovery = async () => {
    try {
      if (mode === 'forgot-password') {
        if (!form.identifier.trim()) return setError('Enter your username or email.');
        const res = await api.forgotPassword(form.identifier);
        setNotice(`${res.message} The reset link is valid for 5 minutes.`);
      } else if (mode === 'forgot-username') {
        if (!form.email.trim()) return setError('Enter your email.');
        const res = await api.forgotUsername(form.email);
        setNotice(res.message);
      } else if (mode === 'reset') {
        if (form.password.length < 8) return setError('New password must be at least 8 characters.');
        if (form.password !== form.confirm) return setError('Passwords do not match.');
        const data = await api.resetPassword(resetToken, form.password);
        setToken(data.access_token);
        onLogin();
      }
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
    }
  };

  const submit = async () => {
    setError('');
    setNotice('');
    setLoading(true);
    try {
      if (mode === 'login' || mode === 'register') await submitLogin();
      else await submitRecovery();
    } finally {
      setLoading(false);
    }
  };

  const onEnter = e => e.key === 'Enter' && submit();
  const isRecovery = mode in TITLES;
  const buttonLabel = {
    login: 'Sign In',
    register: 'Create Account',
    'forgot-password': 'Email Reset Link',
    'forgot-username': 'Email My Username',
    reset: 'Set Password & Sign In',
  }[mode];
  const linkStyle = { background: 'none', border: 'none', padding: 0, color: 'var(--accent)', cursor: 'pointer', fontSize: 13 };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--bg)'
    }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 20px' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--accent)', letterSpacing: '0.1em' }}>PORTFOLIO</div>
          <div className="muted" style={{ marginTop: 6 }}>Private Investment Tracker</div>
        </div>
        <div className="card">
          {isRecovery ? (
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 20 }}>{TITLES[mode]}</div>
          ) : (
            <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
              <button className={mode === 'login' ? 'btn' : 'btn-ghost'} style={{ flex: 1 }} onClick={() => switchMode('login')}>Sign In</button>
              <button className={mode === 'register' ? 'btn' : 'btn-ghost'} style={{ flex: 1 }} onClick={() => switchMode('register')}>Register</button>
            </div>
          )}

          {mode === 'forgot-password' && (
            <div className="form-group">
              <label>Username or email</label>
              <input value={form.identifier} onChange={e => setForm({ ...form, identifier: e.target.value })}
                onKeyDown={onEnter} placeholder="username or email@example.com" autoFocus />
            </div>
          )}

          {mode === 'forgot-username' && (
            <div className="form-group">
              <label>Email</label>
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                onKeyDown={onEnter} placeholder="email@example.com" autoFocus />
            </div>
          )}

          {(mode === 'login' || mode === 'register') && (
            <div className="form-group">
              <label>Username</label>
              <input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })}
                onKeyDown={onEnter} placeholder="username" autoFocus />
            </div>
          )}
          {mode === 'register' && (
            <div className="form-group">
              <label>Email</label>
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="email@example.com" />
            </div>
          )}
          {(mode === 'login' || mode === 'register' || mode === 'reset') && (
            <div className="form-group">
              <label>{mode === 'reset' ? 'New password' : 'Password'}</label>
              <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                onKeyDown={onEnter} placeholder="••••••••" autoFocus={mode === 'reset'} />
            </div>
          )}
          {mode === 'reset' && (
            <div className="form-group">
              <label>Confirm new password</label>
              <input type="password" value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })}
                onKeyDown={onEnter} placeholder="••••••••" />
            </div>
          )}

          {error && <div className="loss" style={{ marginBottom: 16, fontSize: 13 }}>{error}</div>}
          {notice && <div className="gain" style={{ marginBottom: 16, fontSize: 13 }}>{notice}</div>}
          <button className="btn" style={{ width: '100%' }} onClick={submit} disabled={loading}>
            {loading ? 'Please wait...' : buttonLabel}
          </button>

          {mode === 'login' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
              <button style={linkStyle} onClick={() => switchMode('forgot-password')}>Forgot password?</button>
              <button style={linkStyle} onClick={() => switchMode('forgot-username')}>Forgot username?</button>
            </div>
          )}
          {isRecovery && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
              <button style={linkStyle} onClick={() => switchMode('login')}>Back to sign in</button>
              {mode === 'reset' && (
                <button style={linkStyle} onClick={() => switchMode('forgot-password')}>Request a new link</button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
